import './overview.scss';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import moment from 'jalali-moment';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { AppstoreOutlined, ReloadOutlined } from '@ant-design/icons';
import { Page } from '../../../../components/page/page';
import { Input } from '../../../../components/input/input';
import { Button, buttonType } from '../../../../components/button/button';
import { ReactComponent as CalendarIcon } from 'assets/icons/overview/Calendar.svg';
import { CountBuyAndSell } from './charts/count-buy-and-sell/count-buy-and-sell';
import { PMCountBuyAndSell } from './charts/pm-count-buy-and-sell/pm-count-buy-and-sell';
import { CryptoCountBuyAndSell } from './charts/crypto-count-buy-and-sell/crypto-count-buy-and-sell';
import { SumBuyAndSell } from './charts/sum-buy-and-sell/sum-buy-and-sell';
import { PMSumBuyAndSell } from './charts/pm-sum-buy-and-sell/pm-sum-buy-and-sell';
import { CryptoSumBuyAndSell } from './charts/crypto-sum-buy-and-sell/crypto-sum-buy-and-sell';
import { USDTWithdrawalCount } from './charts/usdt-withdrawal-count/usdt-withdrawal-count';
import { SumBuyAndSellCommissions } from './charts/sum-buy-and-sell-commissions/sum-buy-and-sell-commissions';
import { UsersChart } from './charts/users/users';
import { FirstBuys } from './charts/first-buys/first-buys';
import { Balances } from './balances/balances';
import { TrxUsdtReport } from './trx-usdt-report/trx-usdt-report';
import { DateFilterValues } from '../../../../core/domain/overview/overview';
import { AuthenticatedUsersCountChartProps } from '../../../../core/domain/overview/authenticated-users-count';
import { CountBuyAndSellChartProps } from '../../../../core/domain/overview/count-buy-and-sell';
import { CryptoCountBuyAndSellChartProps } from '../../../../core/domain/overview/crypto-count-buy-and-sell';
import { CryptoSumBuyAndSellChartProps } from '../../../../core/domain/overview/crypto-sum-buy-and-sell';
import { PMCountBuyAndSellChartProps } from '../../../../core/domain/overview/pm-count-buy-and-sell';
import { PMSumBuyAndSellChartProps } from '../../../../core/domain/overview/pm-sum-buy-and-sell';
import { RegisteredUsersCountChartProps } from '../../../../core/domain/overview/registered-users-count';
import { SumBuyAndSellChartProps } from '../../../../core/domain/overview/sum-buy-and-sell';
import { SumBuyAndSellCommissionsChartProps } from '../../../../core/domain/overview/sum-buy-and-sell-commissions';
import { UploadedDocumentsChartProps } from '../../../../core/domain/overview/uploaded-documents';
import { USDTWithdrawalCountChartProps } from '../../../../core/domain/overview/usdt-withdrawal-count';
import { FirstBuysChartProps } from '../../../../core/domain/overview/first-buys';
import { BalancesProps } from '../../../../core/domain/overview/balances';
import {
    getAuthenticatedUsersCount,
    getBalances,
    getCountBuyAndSell,
    getCryptoCountBuyAndSell,
    getCryptoSumBuyAndSell,
    getFirstBuys,
    getPMCountBuyAndSell,
    getPMSumBuyAndSell,
    getRegisteredUsersCount,
    getSumBuyAndSell,
    getSumBuyAndSellCommissions,
    getUploadedDocuments,
    getUSDTWithdrawalCount,
} from '../../../../core/repositories/overview';
import { jFormat } from 'core/utils/jFormat';
import { ToFa } from 'core/utils/decimalExtentions';

const jDate = (m: moment.Moment) => m.format('jYYYY/jMM/jDD');

const initialFilters = {
    startDate: moment().subtract(7, 'days').format('jYYYY/jMM/jDD'),
    endDate: '',
};

const emptyFilters = {
    startDate: '',
    endDate: '',
};

/** بازه‌های آماده؛ فقط مقادیر همان فیلتر تاریخ را پر می‌کنند. */
const PRESETS: { key: string; label: string; values: () => DateFilterValues }[] = [
    { key: 'today', label: 'امروز', values: () => ({ startDate: jDate(moment()), endDate: jDate(moment()) }) },
    { key: '7days', label: '۷ روز اخیر', values: () => ({ startDate: jDate(moment().subtract(7, 'days')), endDate: '' }) },
    {
        key: '30days',
        label: '۳۰ روز اخیر',
        values: () => ({ startDate: jDate(moment().subtract(30, 'days')), endDate: '' }),
    },
    { key: 'year', label: 'امسال', values: () => ({ startDate: jDate(moment().startOf('jYear')), endDate: '' }) },
    { key: 'all', label: 'همه', values: () => emptyFilters },
];

export const Overview = () => {
    const [filterValues, setFilterValues] = useState<DateFilterValues>(initialFilters);
    const [activePreset, setActivePreset] = useState<string>('7days');
    const [query, setQuery] = useState('');
    const [reportRange, setReportRange] = useState<DateFilterValues>(initialFilters);
    const [reportRefreshKey, setReportRefreshKey] = useState(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [submitFilterLoading, setSubmitFilterLoading] = useState<boolean>(false);
    const [clearFilterLoading, setClearFilterLoading] = useState<boolean>(false);
    const [authenticatedUsersCountData, setAuthenticatedUsersCountData] = useState<AuthenticatedUsersCountChartProps>();
    const [countBuyAndSellData, setCountBuyAndSellData] = useState<CountBuyAndSellChartProps>();
    const [cryptoCountBuyAndSellData, setCryptoCountBuyAndSellData] = useState<CryptoCountBuyAndSellChartProps>();
    const [cryptoSumBuyAndSellData, setCryptoSumBuyAndSellData] = useState<CryptoSumBuyAndSellChartProps>();
    const [pmCountBuyAndSellData, setPMCountBuyAndSellData] = useState<PMCountBuyAndSellChartProps>();
    const [pmSumBuyAndSellData, setPMSumBuyAndSellData] = useState<PMSumBuyAndSellChartProps>();
    const [registeredUsersCountData, setRegistersUsersCountData] = useState<RegisteredUsersCountChartProps>();
    const [sumBuyAndSellData, setSumBuyAndSellData] = useState<SumBuyAndSellChartProps>();
    const [sumBuyAndSellCommissionsData, setSumBuyAndSellCommissionsData] =
        useState<SumBuyAndSellCommissionsChartProps>();
    const [uploadedDocumentsData, setUploadedDocumentsData] = useState<UploadedDocumentsChartProps>();
    const [usdtWithdrawalCountData, setUSDTWithdrawalCountData] = useState<USDTWithdrawalCountChartProps>();
    const [firstBuysData, setFirstBuysData] = useState<FirstBuysChartProps>();
    const [balances, setBalances] = useState<BalancesProps>();

    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    const buildQuery = (values: DateFilterValues) => {
        let _query = '';

        if (values.startDate !== '')
            _query += `${_query.includes('?') ? '&' : '?'}startDate=${jFormat(values.startDate)}`;
        if (values.endDate !== '') _query += `${_query.includes('?') ? '&' : '?'}endDate=${jFormat(values.endDate)}`;

        return _query;
    };

    const filterHandle = () => {
        setReportRange({ ...filterValues });
        setReportRefreshKey(value => value + 1);
        const _query = buildQuery(filterValues);
        setQuery(_query);
        fetchAllData(setSubmitFilterLoading, _query);
    };

    const presetHandle = (preset: (typeof PRESETS)[number]) => {
        const values = preset.values();
        setReportRange({ ...values });
        setReportRefreshKey(value => value + 1);
        setActivePreset(preset.key);
        setFilterValues(values);
        const _query = buildQuery(values);
        setQuery(_query);
        fetchAllData(setSubmitFilterLoading, _query);
    };

    const fetchAllData = async (setLoading: Dispatch<SetStateAction<boolean>>, query?: string) => {
        await getCountBuyAndSell(setLoading, setCountBuyAndSellData, query);
        getCryptoCountBuyAndSell(setLoading, setCryptoCountBuyAndSellData, query);
        getCryptoSumBuyAndSell(setLoading, setCryptoSumBuyAndSellData, query);
        getPMCountBuyAndSell(setLoading, setPMCountBuyAndSellData, query);
        getPMSumBuyAndSell(setLoading, setPMSumBuyAndSellData, query);
        getSumBuyAndSell(setLoading, setSumBuyAndSellData, query);
        getSumBuyAndSellCommissions(setLoading, setSumBuyAndSellCommissionsData, query);
        getAuthenticatedUsersCount(setLoading, setAuthenticatedUsersCountData, query);
        getRegisteredUsersCount(setLoading, setRegistersUsersCountData, query);
        getUploadedDocuments(setLoading, setUploadedDocumentsData, query);
        getUSDTWithdrawalCount(setLoading, setUSDTWithdrawalCountData, query);
        getFirstBuys(setLoading, setFirstBuysData, query);
        getBalances(setLoading, setBalances);
    };

    useEffect(() => {
        fetchAllData(setLoading, `?startDate=${jFormat(moment().subtract(7, 'days').format('jYYYY/jMM/jDD'))}`);
    }, []);

    const busy = loading || submitFilterLoading || clearFilterLoading;

    const rangeLabel = filterValues.startDate
        ? `از ${ToFa(filterValues.startDate)}${filterValues.endDate ? ` تا ${ToFa(filterValues.endDate)}` : ' تاکنون'}`
        : 'کل بازه زمانی';

    return (
        <Page className="pmx-overview">
            <header className="overview-hero">
                <div className="identity">
                    <span className="icon">
                        <AppstoreOutlined />
                    </span>
                    <div className="titles">
                        <h1>پیشخوان</h1>
                        <p>نمای کلی موجودی‌ها، سفارش‌ها و کاربران</p>
                    </div>
                </div>
                <div className="meta">
                    <span className="range-chip">
                        <CalendarIcon />
                        {rangeLabel}
                    </span>
                    <button
                        type="button"
                        className={`refresh ${busy ? 'busy' : ''}`}
                        disabled={busy}
                        onClick={() => {
                            setReportRefreshKey(value => value + 1);
                            fetchAllData(setSubmitFilterLoading, query);
                        }}
                    >
                        <ReloadOutlined />
                        به‌روزرسانی
                    </button>
                </div>
            </header>

            <section className="overview-filters">
                <div className="presets">
                    {PRESETS.map((preset) => (
                        <button
                            type="button"
                            key={preset.key}
                            disabled={busy}
                            className={activePreset === preset.key ? 'active' : ''}
                            onClick={() => presetHandle(preset)}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>

                <div className="fields">
                    <DatePicker
                        className="date-picker"
                        value={filterValues.startDate}
                        calendar={persian}
                        ref={startDateRef}
                        locale={persian_fa}
                        render={
                            <div>
                                <Input
                                    value={filterValues.startDate}
                                    icon={<CalendarIcon />}
                                    placeholder="تاریخ"
                                    prefix={'از'}
                                />
                            </div>
                        }
                        onChange={(e) => {
                            setActivePreset('');
                            setFilterValues({ ...filterValues, startDate: e.format() });
                        }}
                    />
                    <DatePicker
                        className="date-picker"
                        value={filterValues.endDate}
                        calendar={persian}
                        ref={endDateRef}
                        locale={persian_fa}
                        render={
                            <div>
                                <Input
                                    value={filterValues.endDate}
                                    icon={<CalendarIcon />}
                                    placeholder="تاریخ"
                                    prefix={'تا'}
                                />
                            </div>
                        }
                        onChange={(e) => {
                            setActivePreset('');
                            setFilterValues({ ...filterValues, endDate: e.format() });
                        }}
                    />
                    <div className="buttons">
                        <Button
                            loading={submitFilterLoading}
                            disabled={submitFilterLoading || loading}
                            text="اعمال فیلتر"
                            type={buttonType.success}
                            onClick={filterHandle}
                        />
                        <Button
                            loading={clearFilterLoading}
                            disabled={clearFilterLoading || loading}
                            text="حذف فیلتر"
                            type={buttonType.error}
                            onClick={() => {
                                setActivePreset('all');
                                setFilterValues(emptyFilters);
                                setReportRange(emptyFilters);
                                setReportRefreshKey(value => value + 1);
                                setQuery('');
                                fetchAllData(setClearFilterLoading);
                            }}
                        />
                    </div>
                </div>
            </section>

            <section className="overview-section">
                <div className="section-head">
                    <h3>موجودی‌ها</h3>
                    <p>موجودی کیف پول‌های سامانه</p>
                </div>
                <Balances balancesData={balances} balancesLoading={busy} />
            </section>

            <TrxUsdtReport range={reportRange} refreshKey={reportRefreshKey} />

            <section className="overview-section">
                <div className="section-head">
                    <h3>نمودارها</h3>
                    <p>روند سفارش‌ها، کارمزدها و کاربران در بازه انتخابی</p>
                </div>
                <div className="pmx-charts">
                    <UsersChart
                        authenticatedUsersData={authenticatedUsersCountData}
                        registeredUsersData={registeredUsersCountData}
                        uploadedDocumentsData={uploadedDocumentsData}
                        chartLoading={busy}
                    />
                    <CountBuyAndSell chartData={countBuyAndSellData} chartLoading={busy} />
                    <SumBuyAndSell chartData={sumBuyAndSellData} chartLoading={busy} />
                    <PMCountBuyAndSell chartData={pmCountBuyAndSellData} chartLoading={busy} />
                    <PMSumBuyAndSell chartData={pmSumBuyAndSellData} chartLoading={busy} />
                    <CryptoCountBuyAndSell chartData={cryptoCountBuyAndSellData} chartLoading={busy} />
                    <CryptoSumBuyAndSell chartData={cryptoSumBuyAndSellData} chartLoading={busy} />
                    <USDTWithdrawalCount chartData={usdtWithdrawalCountData} chartLoading={busy} />
                    <SumBuyAndSellCommissions chartData={sumBuyAndSellCommissionsData} chartLoading={busy} />
                    <FirstBuys chartData={firstBuysData} chartLoading={busy} />
                </div>
            </section>
        </Page>
    );
};
